import { useCallback, useEffect, useState } from 'react';

/**
 * useState backed by Local Storage. Falls back to in-memory state when storage
 * is unavailable (private mode, quota errors) so the UI never breaks.
 */
export function useLocalStorage(key, initialValue) {
  const [value, setValue] = useState(() => {
    try {
      const stored = window.localStorage.getItem(key);
      return stored ? JSON.parse(stored) : initialValue;
    } catch {
      return initialValue;
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch {
      /* storage full or blocked — keep the in-memory value */
    }
  }, [key, value]);

  // Keep tabs in sync when the same key changes elsewhere.
  useEffect(() => {
    const onStorage = (event) => {
      if (event.key !== key || event.newValue == null) return;
      try {
        setValue(JSON.parse(event.newValue));
      } catch {
        /* ignore malformed payloads */
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [key]);

  const reset = useCallback(() => setValue(initialValue), [initialValue]);

  return [value, setValue, reset];
}

export default useLocalStorage;
